import type { NextAction } from "@/lib/api/chat";

const ACTION_ID = /^journey_[0-9a-f]{24}$/;
const CONCERN_ID = /^concern_[0-9a-f]{24}$/;
const SLOTS = new Set(["adaptive_continuation", "primary_monetization", "outbound_reentry"]);

/**
 * Validate the untrusted HTTP/SSE action once at the client boundary.
 *
 * An action with a missing or divergent ID must never render: allowing the CTA
 * through would create an eligible decision that cannot be joined to its
 * impression, click, or payment. Callers report the handled degradation and
 * fall back to their existing non-authoritative UI.
 */
/**
 * Extract the action from the SSE envelope `{type, next_action}` that
 * `post_events.next_action_event` puts on the wire.
 *
 * This exists so the streaming and unary chat paths cannot disagree about the
 * wire shape again. On 2026-07-19 the Vercel-SDK hook validated the ENVELOPE
 * instead of its `next_action` field; the envelope has no `action_id`, so every
 * governed action failed canonical identity, was discarded, and was reported as
 * a contract violation (915 in two days). The unary path had always unwrapped
 * correctly — the hook was ported from it without this step.
 *
 * Returns `null` for a missing/!object envelope, so "no action" and "invalid
 * action" stay distinguishable at the call site: only a PRESENT-but-invalid
 * action is a violation worth reporting.
 */
export function nextActionFromStreamEnvelope(data: unknown): unknown {
  return objectValue(data)?.next_action ?? null;
}

export function eligibleGovernedAction(value: unknown): NextAction | null {
  const action = objectValue(value);
  if (!action) return null;
  if (!hasCanonicalIdentity(action)) return null;
  if (!hasGovernedPresentation(action)) return null;
  if (!hasValidPolicy(action)) return null;
  if (!hasEligiblePayload(action)) return null;
  return value as NextAction;
}

function hasCanonicalIdentity(action: Record<string, unknown>): boolean {
  const actionId = stringValue(action.action_id);
  const journeyActionId = stringValue(action.journey_action_id);
  const presentationKey = stringValue(action.presentation_key);
  return ACTION_ID.test(actionId) && actionId === journeyActionId && actionId === presentationKey;
}

function hasGovernedPresentation(action: Record<string, unknown>): boolean {
  const slot = stringValue(action.slot);
  const target =
    slot === "primary_monetization"
      ? stringValue(action.offer_id)
      : stringValue(action.destination);
  return Boolean(
    SLOTS.has(slot) &&
    target &&
    stringValue(action.kind) &&
    stringValue(action.lifecycle_stage) &&
    stringValue(action.reason_code) &&
    stringValue(action.renderer_key) &&
    stringValue(action.placement) &&
    stringValue(action.why)
  );
}

function hasValidPolicy(action: Record<string, unknown>): boolean {
  const policyId = stringValue(action.policy_id);
  const policyVersion = Number(action.policy_version);
  return Boolean(
    policyId &&
    policyId === stringValue(action.source_policy_id) &&
    Number.isInteger(policyVersion) &&
    policyVersion >= 1
  );
}

function hasEligiblePayload(action: Record<string, unknown>): boolean {
  const eligibility = objectValue(action.eligibility);
  return Boolean(
    eligibility?.eligible === true &&
    nonEmptyStrings(eligibility.reasons) &&
    objectValue(action.payload)
  );
}

/** Only attribute an action when its eligible offer is the offer rendered. */
export function governedActionForOffer(
  action: NextAction | null | undefined,
  offerId: string
): NextAction | null {
  return action?.offer_id === offerId ? action : null;
}

/** Camel-case shape owned by UI checkout contexts. */
export interface JourneyActionAnalyticsContext {
  journeyActionId?: string;
  concernId?: string;
  activeConcern?: string;
  journeySlot?: string;
  journeyStage?: string;
  journeyActionKind?: string;
  journeyPolicyId?: string;
  journeyPolicyVersion?: number;
}

/** Build one checkout context projection from the canonical wire action. */
export function governedActionAnalyticsContext(
  action: NextAction | null | undefined
): JourneyActionAnalyticsContext {
  if (!action) return {};
  return {
    journeyActionId: action.journey_action_id,
    concernId: governedActionConcernId(action),
    activeConcern: payloadString(action.payload, "active_concern"),
    journeySlot: action.slot,
    journeyStage: action.lifecycle_stage,
    journeyActionKind: action.kind,
    journeyPolicyId: action.policy_id,
    journeyPolicyVersion: action.policy_version,
  };
}

/** Snake-case projection shared by every owned analytics event. */
export function journeyActionEventProperties(
  value: JourneyActionAnalyticsContext | null | undefined
) {
  const context = value ?? {};
  return {
    journey_action_id: context.journeyActionId,
    concern_id: context.concernId,
    journey_slot: context.journeySlot,
    journey_stage: context.journeyStage,
    journey_action_kind: context.journeyActionKind,
    journey_policy_id: context.journeyPolicyId,
    journey_policy_version: context.journeyPolicyVersion,
  };
}

export function governedActionEventProperties(action: NextAction | null | undefined) {
  return journeyActionEventProperties(governedActionAnalyticsContext(action));
}

export function canonicalJourneyActionId(value: unknown): string | undefined {
  const id = stringValue(value);
  return ACTION_ID.test(id) ? id : undefined;
}

export function canonicalConcernId(value: unknown): string | undefined {
  const id = stringValue(value);
  return CONCERN_ID.test(id) ? id : undefined;
}

/** Extract only a canonical concern identity from an optional action payload. */
export function governedActionConcernId(action: NextAction | null | undefined): string | undefined {
  return canonicalConcernId(payloadString(action?.payload, "concern_id"));
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function nonEmptyStrings(value: unknown): boolean {
  return (
    Array.isArray(value) && value.length > 0 && value.every((item) => Boolean(stringValue(item)))
  );
}

function payloadString(
  payload: NextAction["payload"] | undefined,
  key: string
): string | undefined {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}
